import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import toast from 'react-hot-toast'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import Input from '@/components/common/Input'
import { LangGraphVisualizer } from '@/components/agents'
import { agentStore } from '@/stores/AgentStore'
import { apiClient } from '@/services/api'
import {
  ArrowLeftIcon,
  PlayIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChartBarSquareIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import EvolveAgentModal from '@/components/agents/EvolveAgentModal'
import type { Execution } from '@/types'

const AgentDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [isEditing, setIsEditing] = useState(false)
  const [showGraph, setShowGraph] = useState(false)
  const [evolveOpen, setEvolveOpen] = useState(false)
  const [centralizedAgent, setCentralizedAgent] = useState<any>(null)
  const [editData, setEditData] = useState({
    name: '',
    description: '',
    system_prompt: '',
    temperature: 0.7,
    max_tokens: 2000,
  })

  // Detect if this is a centralized agent (name) vs custom agent (UUID)
  const isCentralized = id ? !id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-/) : false

  // Fetch agent details
  useEffect(() => {
    if (!id) return
    if (isCentralized) {
      // Fetch centralized agent by name
      apiClient.getCentralizedAgents().then(agents => {
        const found = agents.find((a: any) => a.name === id)
        setCentralizedAgent(found || null)
      }).catch(() => setCentralizedAgent(null))
    } else {
      agentStore.fetchAgentById(id)
    }
  }, [id, isCentralized])

  // Fetch agent executions
  const { data: executionsData } = useQuery({
    queryKey: ['agent-executions', id],
    queryFn: () => apiClient.getExecutions({ agent_id: id, limit: 10 }),
    enabled: !!id && !isCentralized,
  })

  const agent = isCentralized ? centralizedAgent : agentStore.selectedAgent

  useEffect(() => {
    if (agent && !isEditing) {
      setEditData({
        name: agent.name,
        description: agent.description || '',
        system_prompt: agent.system_prompt || '',
        temperature: agent.temperature || 0.7,
        max_tokens: agent.max_tokens || 2000,
      })
    }
  }, [agent, isEditing])

  const handleSave = async () => {
    if (!id) return

    try {
      await agentStore.updateAgent(id, editData)
      toast.success('Agent updated successfully')
      setIsEditing(false)
    } catch (error) {
      toast.error('Failed to update agent')
    }
  }

  const handleDelete = async () => {
    if (!id || !agent) return

    if (window.confirm(`Are you sure you want to delete "${agent.name}"?`)) {
      try {
        await agentStore.deleteAgent(id)
        toast.success('Agent deleted successfully')
        navigate('/agents')
      } catch (error) {
        toast.error('Failed to delete agent')
      }
    }
  }

  const handleExecute = () => {
    navigate(`/agents/${id}/execute`)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />
      case 'failed':
        return <XCircleIcon className="w-5 h-5 text-red-500" />
      case 'running':
        return <ClockIcon className="w-5 h-5 text-blue-500 animate-spin" />
      default:
        return <ClockIcon className="w-5 h-5 text-muted-foreground" />
    }
  }

  const getStatusBadgeVariant = (status: string) => {
    const variants: Record<string, 'success' | 'error' | 'warning' | 'gray'> = {
      completed: 'success',
      failed: 'error',
      running: 'warning',
      queued: 'gray',
    }
    return variants[status] || 'gray'
  }

  if (agentStore.isLoading || !agent) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/agents')}>
            <ArrowLeftIcon className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{agent.name}</h1>
              <Badge variant={agent.status === 'active' ? 'success' : 'gray'}>
                {agent.status}
              </Badge>
            </div>
            <p className="text-muted-foreground mt-1">{agent.description || 'No description'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            onClick={handleExecute}
            disabled={agent.status !== 'active'}
          >
            <PlayIcon className="w-5 h-5 mr-2" />
            Execute
          </Button>
          <Button
            variant="secondary"
            onClick={() => setEvolveOpen(true)}
            title="Build V2 of this agent using NeuralGraph context (UES)"
          >
            <SparklesIcon className="w-5 h-5 mr-2" />
            Evolve
          </Button>
          <Button
            variant={showGraph ? 'primary' : 'secondary'}
            onClick={() => setShowGraph(!showGraph)}
          >
            <ChartBarSquareIcon className="w-5 h-5 mr-2" />
            {showGraph ? 'Hide Graph' : 'View Graph'}
          </Button>
          {!isEditing ? (
            <Button variant="secondary" onClick={() => setIsEditing(true)}>
              <PencilIcon className="w-5 h-5 mr-2" />
              Edit
            </Button>
          ) : (
            <>
              <Button variant="primary" onClick={handleSave}>
                Save
              </Button>
              <Button variant="secondary" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
            </>
          )}
          <Button variant="danger" onClick={handleDelete}>
            <TrashIcon className="w-5 h-5 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* LangGraph Visualization */}
      {showGraph && agent.name && (
        <Card className="mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Execution Graph</h3>
          <LangGraphVisualizer
            agentName={agent.name}
            height={400}
          />
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">Configuration</h3>
            <div className="space-y-4">
              {isEditing ? (
                <>
                  <Input
                    label="Name"
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  />
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Description
                    </label>
                    <textarea
                      value={editData.description}
                      onChange={(e) =>
                        setEditData({ ...editData, description: e.target.value })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      System Prompt
                    </label>
                    <textarea
                      value={editData.system_prompt}
                      onChange={(e) =>
                        setEditData({ ...editData, system_prompt: e.target.value })
                      }
                      rows={6}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Temperature: {editData.temperature}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={editData.temperature}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            temperature: parseFloat(e.target.value),
                          })
                        }
                        className="w-full"
                      />
                    </div>
                    <Input
                      label="Max Tokens"
                      type="number"
                      value={editData.max_tokens}
                      onChange={(e) =>
                        setEditData({ ...editData, max_tokens: parseInt(e.target.value) })
                      }
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Framework</p>
                      <p className="text-sm font-medium text-foreground capitalize">
                        {agent.framework}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Model</p>
                      <p className="text-sm font-medium text-foreground">{agent.model}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Temperature</p>
                      <p className="text-sm font-medium text-foreground">
                        {agent.temperature || 0.7}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Max Tokens</p>
                      <p className="text-sm font-medium text-foreground">
                        {agent.max_tokens || 2000}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">System Prompt</p>
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <pre className="text-sm text-foreground whitespace-pre-wrap font-mono">
                        {agent.system_prompt || 'No system prompt'}
                      </pre>
                    </div>
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Tools */}
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">Tools</h3>
            {agent.tools && agent.tools.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {agent.tools.map((tool, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-500/15 text-primary-800"
                  >
                    {tool}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No tools configured</p>
            )}
          </Card>

          {/* Execution History */}
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Recent Executions
            </h3>
            {executionsData?.items && executionsData.items.length > 0 ? (
              <div className="space-y-3">
                {executionsData.items.map((execution: Execution) => (
                  <div
                    key={execution.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => navigate(`/executions/${execution.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(execution.status)}
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          Execution #{execution.id.slice(0, 8)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(execution.created_at), {
                            addSuffix: true,
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {execution.duration && (
                        <span className="text-xs text-muted-foreground">
                          {execution.duration.toFixed(2)}s
                        </span>
                      )}
                      {execution.cost && (
                        <span className="text-xs text-muted-foreground">
                          ${execution.cost.toFixed(4)}
                        </span>
                      )}
                      <Badge variant={getStatusBadgeVariant(execution.status)}>
                        {execution.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <PlayIcon className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground text-sm">No executions yet</p>
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-3"
                  onClick={handleExecute}
                >
                  Run First Execution
                </Button>
              </div>
            )}
          </Card>
        </div>

        {/* Metadata Sidebar */}
        <div className="space-y-6">
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">Metadata</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">ID</p>
                <p className="text-xs font-mono text-foreground">{agent.id}</p>
              </div>
              {agent.created_at && (
              <div>
                <p className="text-sm text-muted-foreground">Created</p>
                <p className="text-sm text-foreground">
                  {formatDistanceToNow(new Date(agent.created_at), { addSuffix: true })}
                </p>
              </div>
              )}
              {agent.updated_at && (
              <div>
                <p className="text-sm text-muted-foreground">Last Updated</p>
                <p className="text-sm text-foreground">
                  {formatDistanceToNow(new Date(agent.updated_at), { addSuffix: true })}
                </p>
              </div>
              )}
              {agent.workspace_id && (
                <div>
                  <p className="text-sm text-muted-foreground">Workspace</p>
                  <p className="text-xs font-mono text-foreground">{agent.workspace_id}</p>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">Statistics</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total Executions</span>
                <span className="text-lg font-semibold text-foreground">
                  {executionsData?.total || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Success Rate</span>
                <span className="text-lg font-semibold text-green-600">
                  {executionsData?.items
                    ? (
                        (executionsData.items.filter((e: Execution) => e.status === 'completed')
                          .length /
                          executionsData.items.length) *
                        100
                      ).toFixed(0)
                    : 0}
                  %
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* UES Phase E — Evolve Agent modal */}
      <EvolveAgentModal
        open={evolveOpen}
        agentName={agent.name}
        onClose={() => setEvolveOpen(false)}
        onDispatched={(requestId: string) => {
          setEvolveOpen(false)
          navigate(`/playground?agent=auto-orchestrator&replay=${requestId}`)
        }}
      />
    </div>
  )
})

export default AgentDetailPage
